import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys, useSiteScope, useSiteScopeRef } from '@/api/queryKeys';
import type { ListParams } from '@/api/types';
import { normalizeError } from '@/api/errors';

import {
  employeesApi,
  employeeFileTypesApi,
  identityDocumentTypesApi,
  type AcademicBackground,
  type AcademicBackgroundInput,
  type EmployeeAssignment,
  type EmployeeInput,
  type EmploymentAssignmentInput,
  type FamilyDependent,
  type FamilyDependentInput,
  type PensionSystem,
  type PensionSystemInput,
  type TerminationInput,
} from '../api/employeesApi';

/**
 * The catalog of expediente file types.
 *
 * `staleTime: Infinity` because it is reference data that changes only when
 * RR.HH. edits it — the picker re-opens constantly and the list is tiny.
 */
export function useEmployeeFileTypes() {
  return useQuery({
    queryKey: queryKeys.catalog.employeeFileTypes.all(),
    queryFn: () => employeeFileTypesApi.list({ page_size: 100 }),
    staleTime: Infinity,
  });
}

export function useEmployees(params: ListParams) {
  const scope = useSiteScope();

  return useQuery({
    queryKey: queryKeys.employees.list(scope, params),
    queryFn: () => employeesApi.list(params),
  });
}

/**
 * The lean list rows. Same filters/ordering as `useEmployees`, but the payload
 * only carries the grid's columns — the main list keeps feeding the ficha and
 * the search picker, which need the expensive serializer.
 */
export function useEmployeeTable(params: ListParams) {
  const scope = useSiteScope();

  return useQuery({
    queryKey: queryKeys.employees.table(scope, params),
    queryFn: () => employeesApi.getTable(params),
  });
}

/**
 * The fotocheck print grid. `staleTime` stays under the shortest photo URL
 * expiry we run with (`employee.file.download.expires_seconds`, 5 min), so a
 * revisited page re-signs instead of painting with dead links.
 */
export function useIdBadges(params: ListParams) {
  const scope = useSiteScope();

  return useQuery({
    queryKey: queryKeys.employees.idBadges(scope, params),
    queryFn: () => employeesApi.getIdBadges(params),
    staleTime: 4 * 60_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Cuántos contratos vigentes terminan dentro de la ventana configurada. El
 * contador usa el mismo predicado que el amarillo de la tabla, así que una
 * mutación que invalida `employees.all(scope)` refresca ambos. Sin `staleTime`
 * a propósito: la antigüedad del warning importa, el backend lo calcula barato.
 */
export function useContractsExpiring() {
  const scope = useSiteScope();

  return useQuery({
    queryKey: queryKeys.employees.summary(scope),
    queryFn: () => employeesApi.getContractsExpiring(),
  });
}

export function useEmployee(id: string) {
  const scope = useSiteScope();

  return useQuery({
    queryKey: queryKeys.employees.detail(scope, id),
    queryFn: () => employeesApi.get(id),
    enabled: Boolean(id),
  });
}

/** La propia ficha (`/employees/me/`) — sin permiso de listado. */
export function useMyProfile(enabled = true) {
  return useQuery({
    queryKey: queryKeys.employees.me(),
    queryFn: () => employeesApi.me(),
    enabled,
  });
}

/**
 * The signed URL of the caller's own profile photo, for the avatars outside the
 * ficha (Topbar, home hero). Resolves to `null` while there is no photo, no
 * employee record, or the URL could not be minted — every caller then falls
 * back to initials, which is the whole fallback contract of `Avatar`.
 *
 * The two queries underneath are shared with the ficha (`useMyProfile`) and
 * the detail header (`useEmployeeFileUrl`), so mounting it in the chrome adds
 * no request the rest of the app would not already make. Chrome that can render
 * logged out passes `enabled = false` so no `employees/me` request fires
 * without a session behind it.
 */
export function useMyProfilePhotoUrl(enabled = true): string | null {
  const profile = useMyProfile(enabled);
  const photo = useEmployeeFileUrl(profile.data?.id ?? '', profile.data?.profile_photo?.id ?? null);
  return photo.data ?? null;
}

export function useIdentityDocumentTypes() {
  return useQuery({
    queryKey: queryKeys.employees.documentTypes.list({ page_size: 500 }),
    queryFn: () => identityDocumentTypesApi.list({ page_size: 500 }),
  });
}

export function useCreateEmployee() {
  const queryClient = useQueryClient();
  const scopeRef = useSiteScopeRef();

  return useMutation({
    mutationFn: (payload: EmployeeInput) => employeesApi.create(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.all(scopeRef()),
      });
    },
  });
}

export function useUpdateEmployee() {
  const queryClient = useQueryClient();
  const scopeRef = useSiteScopeRef();

  return useMutation({
    // The reason is not optional: RN-25 requires edits to record why.
    mutationFn: ({ id, payload }: { id: string; payload: EmployeeInput & { reason: string } }) =>
      employeesApi.update(id, payload),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.all(scopeRef()),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.detail(scopeRef(), id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.assignments.all(scopeRef(), id),
      });
    },
  });
}

/**
 * The cese — the only way out for a worker. It closes the placement too, so
 * the whole employees subtree (detail, history, table) goes stale at once.
 */
export function useTerminateEmployee() {
  const queryClient = useQueryClient();
  const scopeRef = useSiteScopeRef();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: TerminationInput }) =>
      employeesApi.terminate(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.all(scopeRef()),
      });
    },
  });
}

export function useReactivateEmployee() {
  const queryClient = useQueryClient();
  const scopeRef = useSiteScopeRef();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      employeesApi.reactivate(id, reason),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.all(scopeRef()),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.detail(scopeRef(), id),
      });
    },
  });
}

/**
 * Presigned download URL of an assignment's contract, pinned for the session.
 *
 * The ficha jumps straight to the file in a new tab; re-fetching on window
 * focus would mint a fresh URL when the open document is still valid, so the
 * key never goes stale here. The signed URL's own lifetime lives in the
 * backend parameter `contract.file.download.expires_seconds`.
 */
export function useContractDownloadUrl(employeeId: string, assignmentId: string) {
  const scope = useSiteScope();

  return useQuery({
    queryKey: queryKeys.employees.contract.download(scope, employeeId, assignmentId),
    queryFn: () => employeesApi.contractDownloadUrl(employeeId, assignmentId),
    enabled: Boolean(employeeId) && Boolean(assignmentId),
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

export function useUploadContract() {
  const queryClient = useQueryClient();
  const scopeRef = useSiteScopeRef();

  return useMutation({
    mutationFn: ({
      employeeId,
      assignmentId,
      file,
    }: {
      employeeId: string;
      assignmentId: string;
      file: File;
    }) => employeesApi.uploadContract(employeeId, assignmentId, file),
    onSuccess: (_data, { employeeId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.detail(scopeRef(), employeeId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.assignments.all(scopeRef(), employeeId),
      });
    },
  });
}

export function useDiscardContract() {
  const queryClient = useQueryClient();
  const scopeRef = useSiteScopeRef();

  return useMutation({
    mutationFn: ({ employeeId, assignmentId }: { employeeId: string; assignmentId: string }) =>
      employeesApi.discardContract(employeeId, assignmentId),
    onSuccess: (_data, { employeeId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.detail(scopeRef(), employeeId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.assignments.all(scopeRef(), employeeId),
      });
    },
  });
}

/**
 * Creates a new placement (a transfer, RF-040). Invalidates the same keys as
 * `useUploadContract` — a transfer both changes the active assignment and its
 * history, so both the ficha and the history table must re-read.
 */
export function useCreateAssignment() {
  const queryClient = useQueryClient();
  const scopeRef = useSiteScopeRef();

  return useMutation({
    mutationFn: ({
      employeeId,
      payload,
    }: {
      employeeId: string;
      payload: EmploymentAssignmentInput;
    }) => employeesApi.createAssignment(employeeId, payload),
    onSuccess: (_data, { employeeId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.detail(scopeRef(), employeeId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.assignments.all(scopeRef(), employeeId),
      });
    },
  });
}

/**
 * Edits an existing assignment in place. The backend moves the employee's
 * scope when the *current* row changes placement (RN-24) and records the
 * reason (RN-25); invalidating the same keys as create keeps the ficha and
 * the history table in sync.
 */
export function useUpdateAssignment() {
  const queryClient = useQueryClient();
  const scopeRef = useSiteScopeRef();

  return useMutation({
    mutationFn: ({
      employeeId,
      assignmentId,
      payload,
    }: {
      employeeId: string;
      assignmentId: string;
      payload: EmploymentAssignmentInput;
    }) => employeesApi.updateAssignment(employeeId, assignmentId, payload),
    onSuccess: (_data, { employeeId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.detail(scopeRef(), employeeId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.assignments.all(scopeRef(), employeeId),
      });
    },
  });
}

/**
 * Voids a placement uploaded by mistake. It can reopen the contract a wrong
 * transfer closed and move the worker's scope, so the ficha is refreshed with
 * the history table.
 */
export function useVoidAssignment() {
  const queryClient = useQueryClient();
  const scopeRef = useSiteScopeRef();

  return useMutation({
    mutationFn: ({
      employeeId,
      assignmentId,
      reason,
    }: {
      employeeId: string;
      assignmentId: string;
      reason: string;
    }) => employeesApi.voidAssignment(employeeId, assignmentId, reason),
    onSuccess: (_data, { employeeId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.detail(scopeRef(), employeeId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.assignments.all(scopeRef(), employeeId),
      });
    },
  });
}

/**
 * Creates a new placement (a transfer, RF-040) and, when a contract file is
 * chosen, attaches it in the same mutation.
 *
 * The upload runs after the create because the file needs the new assignment's
 * id. A failed upload does *not* roll back the transfer (there is no delete
 * endpoint): the record is returned with an `attachmentWarning` instead, so the
 * caller can tell the user the placement exists but the file did not attach.
 * Re-submitting the form then reuses the already-created placement instead of
 * minting a duplicate transfer.
 */
export function useCreateAssignmentWithFile() {
  const queryClient = useQueryClient();
  const scopeRef = useSiteScopeRef();

  return useMutation<
    { record: EmployeeAssignment; attachmentWarning: string | null },
    unknown,
    { employeeId: string; payload: EmploymentAssignmentInput; file?: File | null }
  >({
    mutationFn: async ({ employeeId, payload, file }) => {
      const created = await employeesApi.createAssignment(employeeId, payload);
      if (!file) return { record: created, attachmentWarning: null };
      try {
        const record = await employeesApi.uploadContract(employeeId, created.id, file);
        return { record, attachmentWarning: null };
      } catch (caught) {
        return {
          record: created,
          attachmentWarning: `La asignación se creó, pero el contrato no pudo subirse: ${normalizeError(caught).message}`,
        };
      }
    },
    onSuccess: (_result, { employeeId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.detail(scopeRef(), employeeId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.assignments.all(scopeRef(), employeeId),
      });
    },
  });
}

export function useEmploymentAssignments(employeeId: string, params: ListParams) {
  const scope = useSiteScope();

  return useQuery({
    queryKey: queryKeys.employees.assignments.list(scope, employeeId, params),
    queryFn: () => employeesApi.listAssignments(employeeId, params),
    enabled: Boolean(employeeId),
  });
}

/** The worker's family dependents, newest first — feeds the ficha table. */
export function useFamilyDependents(employeeId: string, params: ListParams) {
  const scope = useSiteScope();

  return useQuery({
    queryKey: queryKeys.employees.familyDependents.list(scope, employeeId, params),
    queryFn: () => employeesApi.listFamilyDependents(employeeId, params),
    enabled: Boolean(employeeId),
  });
}

/** The worker's academic backgrounds, newest first — feeds the ficha table. */
export function useAcademicBackgrounds(employeeId: string, params: ListParams) {
  const scope = useSiteScope();

  return useQuery({
    queryKey: queryKeys.employees.academicBackgrounds.list(scope, employeeId, params),
    queryFn: () => employeesApi.listAcademicBackgrounds(employeeId, params),
    enabled: Boolean(employeeId),
  });
}

export function useCreateFamilyDependent() {
  const queryClient = useQueryClient();
  const scopeRef = useSiteScopeRef();

  return useMutation({
    mutationFn: ({ employeeId, payload }: { employeeId: string; payload: FamilyDependentInput }) =>
      employeesApi.createFamilyDependent(employeeId, payload),
    onSuccess: (_data, { employeeId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.familyDependents.all(scopeRef(), employeeId),
      });
    },
  });
}

export function useCreateFamilyDependentWithFile() {
  const queryClient = useQueryClient();
  const scopeRef = useSiteScopeRef();

  return useMutation<
    { record: FamilyDependent; attachmentWarning: string | null },
    unknown,
    { employeeId: string; payload: FamilyDependentInput; file?: File | null }
  >({
    mutationFn: async ({ employeeId, payload, file }) => {
      const created = await employeesApi.createFamilyDependent(employeeId, payload);
      if (!file) return { record: created, attachmentWarning: null };
      try {
        const record = await employeesApi.uploadFamilyDependentFile(employeeId, created.id, file);
        return { record, attachmentWarning: null };
      } catch (caught) {
        return {
          record: created,
          attachmentWarning: `La carga familiar se creó, pero el documento no pudo subirse: ${normalizeError(caught).message}`,
        };
      }
    },
    onSuccess: (_result, { employeeId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.familyDependents.all(scopeRef(), employeeId),
      });
    },
  });
}

export function useUpdateFamilyDependent() {
  const queryClient = useQueryClient();
  const scopeRef = useSiteScopeRef();

  return useMutation({
    mutationFn: ({
      employeeId,
      id,
      payload,
    }: {
      employeeId: string;
      id: string;
      payload: FamilyDependentInput;
    }) => employeesApi.updateFamilyDependent(employeeId, id, payload),
    onSuccess: (_data, { employeeId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.familyDependents.all(scopeRef(), employeeId),
      });
    },
  });
}

/** Deactivates a family dependent — the reason is mandatory (RN-25). */
export function useDeleteFamilyDependent() {
  const queryClient = useQueryClient();
  const scopeRef = useSiteScopeRef();

  return useMutation({
    mutationFn: ({ employeeId, id, reason }: { employeeId: string; id: string; reason: string }) =>
      employeesApi.removeFamilyDependent(employeeId, id, reason),
    onSuccess: (_data, { employeeId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.familyDependents.all(scopeRef(), employeeId),
      });
    },
  });
}

/** Uploads the document of a family dependent (only when it has none yet). */
export function useUploadFamilyDependentFile() {
  const queryClient = useQueryClient();
  const scopeRef = useSiteScopeRef();

  return useMutation({
    mutationFn: ({ employeeId, id, file }: { employeeId: string; id: string; file: File }) =>
      employeesApi.uploadFamilyDependentFile(employeeId, id, file),
    onSuccess: (_data, { employeeId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.familyDependents.all(scopeRef(), employeeId),
      });
    },
  });
}

/**
 * The presigned download URL of a dependent's document, for the pop-up PDF
 * viewer. Pinned with `staleTime`/`gcTime` Infinity for the same reason as the
 * academic background download: the viewer caches the document keyed by URL, so
 * re-fetching would mint a new URL, force a re-download and reset the reader.
 */
export function useFamilyDependentFileUrl(employeeId: string, id: string) {
  const scope = useSiteScope();

  return useQuery({
    queryKey: queryKeys.employees.familyDependents.download(scope, employeeId, id),
    queryFn: () => employeesApi.downloadFamilyDependentUrl(employeeId, id),
    enabled: Boolean(employeeId) && Boolean(id),
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

export function useCreateAcademicBackground() {
  const queryClient = useQueryClient();
  const scopeRef = useSiteScopeRef();

  return useMutation({
    mutationFn: ({
      employeeId,
      payload,
    }: {
      employeeId: string;
      payload: AcademicBackgroundInput;
    }) => employeesApi.createAcademicBackground(employeeId, payload),
    onSuccess: (_data, { employeeId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.academicBackgrounds.all(scopeRef(), employeeId),
      });
    },
  });
}

export function useCreateAcademicBackgroundWithFile() {
  const queryClient = useQueryClient();
  const scopeRef = useSiteScopeRef();

  return useMutation<
    { record: AcademicBackground; attachmentWarning: string | null },
    unknown,
    { employeeId: string; payload: AcademicBackgroundInput; file?: File | null }
  >({
    mutationFn: async ({ employeeId, payload, file }) => {
      const created = await employeesApi.createAcademicBackground(employeeId, payload);
      if (!file) return { record: created, attachmentWarning: null };
      try {
        const record = await employeesApi.uploadAcademicBackgroundFile(
          employeeId,
          created.id,
          file,
        );
        return { record, attachmentWarning: null };
      } catch (caught) {
        return {
          record: created,
          attachmentWarning: `La formación académica se creó, pero el certificado no pudo subirse: ${normalizeError(caught).message}`,
        };
      }
    },
    onSuccess: (_result, { employeeId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.academicBackgrounds.all(scopeRef(), employeeId),
      });
    },
  });
}

export function useUpdateAcademicBackground() {
  const queryClient = useQueryClient();
  const scopeRef = useSiteScopeRef();

  return useMutation({
    mutationFn: ({
      employeeId,
      id,
      payload,
    }: {
      employeeId: string;
      id: string;
      payload: AcademicBackgroundInput;
    }) => employeesApi.updateAcademicBackground(employeeId, id, payload),
    onSuccess: (_data, { employeeId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.academicBackgrounds.all(scopeRef(), employeeId),
      });
    },
  });
}

/** Uploads the certificate of an academic background (replaces, keeps old object). */
export function useUploadAcademicBackgroundFile() {
  const queryClient = useQueryClient();
  const scopeRef = useSiteScopeRef();

  return useMutation({
    mutationFn: ({ employeeId, id, file }: { employeeId: string; id: string; file: File }) =>
      employeesApi.uploadAcademicBackgroundFile(employeeId, id, file),
    onSuccess: (_data, { employeeId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.academicBackgrounds.all(scopeRef(), employeeId),
      });
    },
  });
}

/**
 * The presigned download URL of a certificate, for the pop-up PDF viewer.
 *
 * Pinned with `staleTime`/`gcTime` Infinity on purpose: the viewer caches the
 * document keyed by URL, so re-fetching would mint a new URL, force a re-download
 * and reset the reader. The URL's validity lives in the backend parameter
 * `academic.file.download.expires_seconds`.
 */
export function useAcademicBackgroundFileUrl(employeeId: string, id: string) {
  const scope = useSiteScope();

  return useQuery({
    queryKey: queryKeys.employees.academicBackgrounds.download(scope, employeeId, id),
    queryFn: () => employeesApi.downloadAcademicBackgroundUrl(employeeId, id),
    enabled: Boolean(employeeId) && Boolean(id),
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/** Deactivates an academic background — the reason is mandatory (RN-25). */
export function useDeleteAcademicBackground() {
  const queryClient = useQueryClient();
  const scopeRef = useSiteScopeRef();

  return useMutation({
    mutationFn: ({ employeeId, id, reason }: { employeeId: string; id: string; reason: string }) =>
      employeesApi.removeAcademicBackground(employeeId, id, reason),
    onSuccess: (_data, { employeeId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.academicBackgrounds.all(scopeRef(), employeeId),
      });
    },
  });
}

/**
 * The worker's pension system — a singleton. A 404 means "no hay registro"
 * (the worker has not filled it in yet), surfaced to the caller so the section
 * can render its empty state instead of an error.
 */
export function usePension(employeeId: string) {
  const scope = useSiteScope();

  return useQuery({
    queryKey: queryKeys.employees.pension.detail(scope, employeeId),
    queryFn: () => employeesApi.getPension(employeeId),
    enabled: Boolean(employeeId),
  });
}

/**
 * Saves the pension singleton via POST (the backend upserts, creating or
 * updating the 1-a-1 record). A success invalidates the record's key.
 */
export function useSavePension() {
  const queryClient = useQueryClient();
  const scopeRef = useSiteScopeRef();

  return useMutation({
    mutationFn: ({ employeeId, payload }: { employeeId: string; payload: PensionSystemInput }) =>
      employeesApi.savePension(employeeId, payload),
    onSuccess: (_data, { employeeId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.pension.all(scopeRef(), employeeId),
      });
    },
  });
}

/**
 * Saves the pension singleton (POST upsert) and, when a declaration file is
 * chosen, attaches it in the same mutation.
 *
 * The upload runs after the save because the singleton must exist first. A
 * failed upload leaves the upsert in place (the declaration can be replaced,
 * so re-submitting safely re-attaches); the record is returned with an
 * `attachmentWarning` so the caller can tell the user the affiliation was
 * saved but the file did not attach.
 */
export function useSavePensionWithFile() {
  const queryClient = useQueryClient();
  const scopeRef = useSiteScopeRef();

  return useMutation<
    { record: PensionSystem; attachmentWarning: string | null },
    unknown,
    { employeeId: string; payload: PensionSystemInput; file?: File | null }
  >({
    mutationFn: async ({ employeeId, payload, file }) => {
      const created = await employeesApi.savePension(employeeId, payload);
      if (!file) return { record: created, attachmentWarning: null };
      try {
        const record = await employeesApi.uploadPensionFile(employeeId, file);
        return { record, attachmentWarning: null };
      } catch (caught) {
        return {
          record: created,
          attachmentWarning: `El sistema de pensiones se guardó, pero la declaración no pudo subirse: ${normalizeError(caught).message}`,
        };
      }
    },
    onSuccess: (_result, { employeeId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.pension.all(scopeRef(), employeeId),
      });
    },
  });
}

/**
 * Uploads (or replaces) the worker's pension declaration. The singleton record
 * must already exist — the caller saves it first, then uploads the chosen file.
 */
export function usePensionUpload() {
  const queryClient = useQueryClient();
  const scopeRef = useSiteScopeRef();

  return useMutation({
    mutationFn: ({ employeeId, file }: { employeeId: string; file: File }) =>
      employeesApi.uploadPensionFile(employeeId, file),
    onSuccess: (_data, { employeeId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.pension.all(scopeRef(), employeeId),
      });
    },
  });
}

/**
 * The presigned download URL of the declaration, for the pop-up PDF viewer.
 *
 * Pinned with `staleTime`/`gcTime` Infinity for the same reason as the contract/
 * academic viewers: the viewer caches the document keyed by URL, so re-fetching
 * would mint a fresh URL, force a re-download and reset the reader. The URL's
 * validity lives in the backend parameter `pension.file.download.expires_seconds`.
 */
export function usePensionFileUrl(employeeId: string) {
  const scope = useSiteScope();

  return useQuery({
    queryKey: queryKeys.employees.pension.download(scope, employeeId),
    queryFn: () => employeesApi.pensionDownloadUrl(employeeId),
    enabled: Boolean(employeeId),
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/** The files attached to the worker's expediente (the active ones by default). */
export function useEmployeeFiles(employeeId: string, params: ListParams = {}) {
  const scope = useSiteScope();

  return useQuery({
    queryKey: queryKeys.employees.files.list(scope, employeeId, params),
    queryFn: () => employeesApi.listFiles(employeeId, params),
    enabled: Boolean(employeeId),
  });
}

/**
 * The presigned URL of an attached file, for the avatar's `<img src>`.
 *
 * The URL is a short-lived grant; its validity lives in the backend parameter
 * `employee.file.download.expires_seconds` (300 s), so re-minting it on every
 * render would restart the image load and re-audit the read. `staleTime` is
 * finite but well *under* that expiry: a stale-but-cached URL is still within
 * its validity window, so a remount shows the cached face immediately and
 * re-mints in the background instead of fetching the bytes twice. `gcTime`
 * stays Infinity so the entry is never dropped, and an expired URL degrades to
 * the initials (via the Avatar's per-URL failure) until the fresh URL lands.
 */
export function useEmployeeFileUrl(employeeId: string, fileId: string | null) {
  const scope = useSiteScope();

  return useQuery({
    queryKey: queryKeys.employees.files.download(scope, employeeId, fileId ?? ''),
    queryFn: () => employeesApi.downloadFileUrl(employeeId, fileId as string),
    enabled: Boolean(employeeId) && Boolean(fileId),
    staleTime: 4 * 60_000,
    gcTime: Infinity,
    retry: false,
  });
}

/**
 * Attaches (or replaces) a file on the worker's expediente.
 *
 * Invalidates the employee's own `detail` key because the detail response
 * inlines `profile_photo`: a photo upload that left the cached employee stale
 * would keep showing the old face until a manual refetch.
 */
export function useEmployeeFileUpload() {
  const queryClient = useQueryClient();
  const scopeRef = useSiteScopeRef();

  return useMutation({
    mutationFn: ({
      employeeId,
      fileTypeId,
      file,
      note,
    }: {
      employeeId: string;
      fileTypeId: string;
      file: File;
      note?: string;
    }) => employeesApi.uploadFile(employeeId, fileTypeId, file, note),
    // `employee.detail` is enough to retire the old signed URL too: replacing a
    // photo creates a *new* EmployeeFile, so the inlined `profile_photo.id`
    // changes and the download query gets a different key on the next render.
    onSuccess: (_data, { employeeId }) => {
      const scope = scopeRef();
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.files.all(scope, employeeId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.employees.detail(scope, employeeId),
      });
    },
  });
}
